import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Link2, Plus, Copy, Check, Trash2, ToggleLeft, ToggleRight,
  Users, Clock, Shield, ExternalLink, X, Trophy, Flame, Pencil, ArrowUp, ArrowDown, GripVertical
} from 'lucide-react';

const AdminInvites = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newInviteName, setNewInviteName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [inviteUsers, setInviteUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [reordering, setReordering] = useState(false);

  const [copiedTrialLink, setCopiedTrialLink] = useState(false);

  useEffect(() => {
    fetchInvites();
  }, []);

  const fetchInvites = async () => {
    try {
      const res = await apiClient.get('/admin/invites');
      setInvites(res.data);
    } catch (err) {
      console.error('Failed to fetch invites:', err);
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  const createInvite = async () => {
    if (!newInviteName.trim()) {
      toast.error('Please enter a name for the invite');
      return;
    }

    setCreating(true);
    try {
      const res = await apiClient.post('/admin/invites', {
        name: newInviteName.trim(),
        is_reusable: true
      });
      setInvites([res.data, ...invites]);
      setNewInviteName('');
      setShowCreateForm(false);
      toast.success('Invite link created!');
    } catch (err) {
      console.error('Create invite error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create invite link';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const toggleInviteStatus = async (invite) => {
    try {
      if (invite.is_active) {
        await apiClient.put(`/admin/invites/${invite.id}/deactivate`);
        toast.success('Invite deactivated');
      } else {
        await apiClient.put(`/admin/invites/${invite.id}/activate`);
        toast.success('Invite activated');
      }
      fetchInvites();
    } catch (err) {
      toast.error('Failed to update invite status');
    }
  };

  const deleteInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to delete this invite? This cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/admin/invites/${inviteId}`);
      setInvites(invites.filter(inv => inv.id !== inviteId));
      toast.success('Invite deleted');
    } catch (err) {
      toast.error('Failed to delete invite');
    }
  };

  const renameInvite = async (inviteId) => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      await apiClient.put(`/admin/invites/${inviteId}/rename`, { name: editName.trim() });
      setInvites(invites.map(inv => inv.id === inviteId ? { ...inv, name: editName.trim() } : inv));
      setEditingId(null);
      setEditName('');
      toast.success('Invite renamed');
    } catch (err) {
      toast.error('Failed to rename invite');
    }
  };

  const moveInvite = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= invites.length) return;
    const updated = [...invites];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setInvites(updated);
  };

  const saveOrder = async () => {
    try {
      const order = invites.map((inv, i) => ({ id: inv.id, sort_order: i }));
      await apiClient.put('/admin/invites/reorder', { order });
      setReordering(false);
      toast.success('Order saved');
    } catch (err) {
      toast.error('Failed to save order');
      fetchInvites();
    }
  };

  const copyLink = (invite) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/register?invite=${invite.code}`;
    navigator.clipboard.writeText(link);
    setCopiedId(invite.id);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const copyTrialLink = () => {
    const trialLink = `${window.location.origin}/free-trial`;
    navigator.clipboard.writeText(trialLink);
    setCopiedTrialLink(true);
    toast.success('Free trial link copied!');
    setTimeout(() => setCopiedTrialLink(false), 2000);
  };

  const getFullLink = (invite) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/register?invite=${invite.code}`;
  };

  const viewInviteUsers = async (invite) => {
    setSelectedInvite(invite);
    setLoadingUsers(true);
    try {
      const res = await apiClient.get(`/admin/invites/${invite.code}/users`);
      setInviteUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
      setInviteUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-[#EFB342] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const activeInvites = invites.filter(inv => inv.is_active);
  const totalRegistrations = invites.reduce((sum, inv) => sum + inv.registrations_count, 0);

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 flex items-center gap-3">
              <Link2 className="text-[#EFB342]" />
              INVITE LINKS
            </h1>
            <p className="text-stone-600 mt-1">Manage registration access for paid subscribers</p>
          </div>
          <div className="flex items-center gap-2">
            {reordering ? (
              <>
                <Button
                  onClick={() => { setReordering(false); fetchInvites(); }}
                  variant="outline"
                  className="border-stone-300 text-stone-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveOrder}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                >
                  <Check size={18} className="mr-2" />
                  Save Order
                </Button>
              </>
            ) : (
              <>
                {invites.length > 1 && (
                  <Button
                    onClick={() => setReordering(true)}
                    variant="outline"
                    className="border-stone-300 text-stone-700 hover:text-stone-900"
                  >
                    <GripVertical size={18} className="mr-2" />
                    Re-order
                  </Button>
                )}
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                >
                  <Plus size={18} className="mr-2" />
                  Create Invite
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Shield className="text-green-500" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{activeInvites.length}</p>
                <p className="text-xs text-stone-500">Active Links</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Users className="text-[#EFB342]" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{totalRegistrations}</p>
                <p className="text-xs text-stone-500">Total Registrations</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Link2 className="text-purple-500" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{invites.length}</p>
                <p className="text-xs text-stone-500">Total Links</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Reference Links */}
        <div className="bg-white border border-stone-200 rounded-xl p-5 mb-8" data-testid="quick-reference-links">
          <div className="flex items-center gap-2 mb-4">
            <ExternalLink className="text-[#EFB342]" size={20} />
            <h3 className="font-semibold text-stone-900 text-lg">Quick Reference Links</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'Member Login Page', url: 'https://rxgrit.buildnutrition.ca/login' },
              { label: 'Sales / Sign-Up Page', url: 'https://rxgrit.buildnutrition.ca/signup' },
            ].map((link) => (
              <div key={link.label} className="flex items-center justify-between bg-stone-50 border border-stone-300 rounded-lg px-4 py-3">
                <div className="min-w-0 mr-3">
                  <p className="text-sm text-stone-600 mb-0.5">{link.label}</p>
                  <p className="text-[#EFB342] text-sm truncate">{link.url}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-stone-300 text-stone-700 hover:text-stone-900 flex-shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(link.url);
                    toast.success(`${link.label} URL copied!`);
                  }}
                >
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Free Trial Link Section */}
        <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-xl p-5 mb-8" data-testid="free-trial-link-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Clock className="text-blue-400" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-lg">Free Trial Link</h3>
                <p className="text-stone-600 text-sm">7-day trial for prospective clients (no invite code needed)</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <code className="bg-white px-3 py-2 rounded-lg text-sm text-blue-300 hidden sm:block">
                {window.location.origin}/free-trial
              </code>
              <Button
                onClick={copyTrialLink}
                variant="outline"
                className={`border-blue-500/50 ${copiedTrialLink ? 'text-green-400 border-green-500' : 'text-blue-400 hover:text-blue-300'}`}
              >
                {copiedTrialLink ? <Check size={18} /> : <Copy size={18} />}
                <span className="ml-2">{copiedTrialLink ? 'Copied!' : 'Copy Link'}</span>
              </Button>
              <a 
                href="/free-trial" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 text-stone-600 hover:text-stone-900 transition-colors"
                title="Open in new tab"
              >
                <ExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Create Form Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-[#F5F4F1]/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white border border-stone-200 rounded-2xl p-6 w-full max-w-md">
              <h2 className="font-heading text-xl font-bold text-stone-900 mb-4">Create New Invite Link</h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-stone-700">Invite Name</Label>
                  <Input
                    value={newInviteName}
                    onChange={(e) => setNewInviteName(e.target.value)}
                    placeholder="e.g., Main Registration Link"
                    className="bg-stone-50 border-stone-300 text-stone-900"
                  />
                  <p className="text-xs text-stone-500">A friendly name to help you identify this link</p>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => { setShowCreateForm(false); setNewInviteName(''); }}
                  className="flex-1 border-stone-300 text-stone-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createInvite}
                  disabled={creating || !newInviteName.trim()}
                  className="flex-1 bg-[#EFB342] text-black hover:bg-[#D99F30]"
                >
                  {creating ? 'Creating...' : 'Create Link'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invites List */}
        <div className="space-y-4">
          {invites.length === 0 ? (
            <div className="text-center py-16 bg-white border border-stone-200 rounded-2xl">
              <Link2 className="mx-auto text-stone-400 mb-4" size={48} />
              <p className="text-stone-600 text-lg mb-2">No invite links yet</p>
              <p className="text-stone-500 text-sm mb-6">Create your first invite link to start accepting registrations</p>
              <Button
                onClick={() => setShowCreateForm(true)}
                className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
              >
                <Plus size={18} className="mr-2" />
                Create Your First Invite
              </Button>
            </div>
          ) : (
            invites.map((invite, index) => (
              <div
                key={invite.id}
                className={`bg-white border rounded-2xl p-6 ${
                  invite.is_active ? 'border-stone-200' : 'border-red-500/30 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  {reordering && (
                    <div className="flex flex-col gap-1 mr-2 pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveInvite(index, -1)}
                        disabled={index === 0}
                        className="h-7 w-7 p-0 text-stone-600 hover:text-[#EFB342] disabled:opacity-30"
                      >
                        <ArrowUp size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveInvite(index, 1)}
                        disabled={index === invites.length - 1}
                        className="h-7 w-7 p-0 text-stone-600 hover:text-[#EFB342] disabled:opacity-30"
                      >
                        <ArrowDown size={16} />
                      </Button>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {editingId === invite.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') renameInvite(invite.id);
                              if (e.key === 'Escape') { setEditingId(null); setEditName(''); }
                            }}
                            className="bg-stone-50 border-stone-300 text-stone-900 h-8 text-sm w-64"
                            autoFocus
                          />
                          <Button size="sm" onClick={() => renameInvite(invite.id)} className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-8 px-3">
                            <Check size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditName(''); }} className="text-stone-600 hover:text-stone-900 h-8 px-2">
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <h3 className="font-semibold text-stone-900 text-lg">{invite.name}</h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingId(invite.id); setEditName(invite.name); }}
                            className="text-stone-500 hover:text-[#EFB342] h-7 w-7 p-0"
                            title="Rename"
                          >
                            <Pencil size={14} />
                          </Button>
                        </>
                      )}
                      {editingId !== invite.id && (
                        <>
                          {invite.is_active ? (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">Inactive</span>
                          )}
                          {invite.is_reusable && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">Unlimited Use</span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-stone-500">
                      <button 
                        onClick={() => viewInviteUsers(invite)}
                        className="flex items-center gap-1 hover:text-[#EFB342] transition-colors cursor-pointer"
                        data-testid={`view-invite-users-${invite.id}`}
                      >
                        <Users size={14} />
                        <span className="underline decoration-dotted underline-offset-2">{invite.registrations_count} registrations</span>
                      </button>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        Created {new Date(invite.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleInviteStatus(invite)}
                      className={invite.is_active 
                        ? 'text-green-400 hover:text-red-400 hover:bg-red-500/10'
                        : 'text-red-400 hover:text-green-400 hover:bg-green-500/10'
                      }
                    >
                      {invite.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteInvite(invite.id)}
                      className="text-stone-600 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>

                {/* Link Display */}
                <div className="bg-stone-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-stone-500 uppercase">Registration Link</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm text-[#EFB342] break-all">
                      {getFullLink(invite)}
                    </code>
                    <Button
                      size="sm"
                      onClick={() => copyLink(invite)}
                      className={copiedId === invite.id 
                        ? 'bg-green-500 text-stone-900'
                        : 'bg-stone-100 text-stone-900 hover:bg-stone-200'
                      }
                    >
                      {copiedId === invite.id ? (
                        <><Check size={16} className="mr-1" /> Copied</>
                      ) : (
                        <><Copy size={16} className="mr-1" /> Copy</>
                      )}
                    </Button>
                    <a
                      href={getFullLink(invite)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors"
                    >
                      <ExternalLink size={16} className="text-stone-600" />
                    </a>
                  </div>
                </div>

                {!invite.is_active && invite.deactivated_at && (
                  <p className="text-xs text-red-400 mt-3">
                    Deactivated on {new Date(invite.deactivated_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-white border border-stone-200 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold text-stone-900 mb-3">How to Use</h3>
          <ol className="space-y-2 text-stone-600 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-[#EFB342] font-bold">1.</span>
              <span>Create an invite link above (you typically only need one active link)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#EFB342] font-bold">2.</span>
              <span>Copy the registration link and add it to your payment confirmation email template</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#EFB342] font-bold">3.</span>
              <span>New paid users will click the link and be able to register immediately</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#EFB342] font-bold">4.</span>
              <span>Monitor registrations here - the link works unlimited times until you deactivate it</span>
            </li>
          </ol>
        </div>

        {/* Users Modal */}
        {selectedInvite && (
          <div className="fixed inset-0 bg-[#F5F4F1]/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white border border-stone-200 rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-xl font-bold text-stone-900">Registrations</h2>
                  <p className="text-stone-600 text-sm mt-1">
                    Users who registered via "{selectedInvite.name}"
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSelectedInvite(null); setInviteUsers([]); }}
                  className="text-stone-600 hover:text-stone-900"
                >
                  <X size={20} />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-[#EFB342] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : inviteUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="mx-auto text-stone-400 mb-4" size={48} />
                    <p className="text-stone-600">No users have registered with this link yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {inviteUsers.map((user, index) => (
                      <div
                        key={user.id}
                        className="bg-stone-50 border border-stone-200 rounded-xl p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#EFB342] to-orange-600 rounded-full flex items-center justify-center text-black font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="font-medium text-stone-900">{user.name}</h4>
                            <p className="text-sm text-stone-500">{user.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1 text-[#EFB342]">
                              <Trophy size={14} />
                              <span>{user.total_points} pts</span>
                            </div>
                            <div className="flex items-center gap-1 text-orange-400">
                              <Flame size={14} />
                              <span>{user.current_streak} streak</span>
                            </div>
                          </div>
                          <p className="text-xs text-stone-500 mt-1">
                            Joined {new Date(user.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-stone-200">
                <p className="text-sm text-stone-500 text-center">
                  {inviteUsers.length} user{inviteUsers.length !== 1 ? 's' : ''} registered with this invite link
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminInvites;
